'use client';
import React, { useState } from 'react';
import { Download, FileText, Globe2, Users, CheckCircle2, Shield, Package, ArrowRight, ChevronLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import type { WizardData } from './VisaWizard';

interface Props {
  data: WizardData;
  updateData: (d: Partial<WizardData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const packageContents = [
  { id: 'pkg-cover', icon: FileText, label: 'AI Cover Letter', detail: 'Embassy-specific, 2 pages, signed format', pages: 2, color: 'blue' },
  { id: 'pkg-itinerary', icon: Globe2, label: 'Travel Itinerary', detail: 'Day-by-day plan, hotel & transport details', pages: 3, color: 'sky' },
  { id: 'pkg-sponsor', icon: Users, label: 'Sponsor Letter', detail: 'Template with your details pre-filled', pages: 1, color: 'violet' },
  { id: 'pkg-checklist', icon: CheckCircle2, label: 'Document Checklist', detail: 'Complete checklist with submission order', pages: 1, color: 'green' },
  { id: 'pkg-passport', icon: Shield, label: 'Passport Copy', detail: 'Bio data page — encrypted upload', pages: 1, color: 'gray' },
  { id: 'pkg-bank', icon: FileText, label: 'Bank Statement', detail: '3-month statement — validated ✓', pages: 4, color: 'gray' },
];

const languages = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'ur', label: 'اردو', flag: '🇵🇰' },
  { code: 'hi', label: 'हिंदी', flag: '🇮🇳' },
];

export default function Step8Download({ data, onNext, onBack }: Props) {
  const [exportLang, setExportLang] = useState('en');
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    // TODO: Call /api/packages/generate-pdf with { applicationId, language: exportLang }
    await new Promise((r) => setTimeout(r, 2200));
    setDownloading(false);
    setDownloaded(true);
    toast.success('Package downloaded — VisaPilot_Germany_Tourist_Package.pdf (2.4 MB)');
  };

  const totalPages = packageContents.reduce((a, c) => a + c.pages, 0);

  return (
    <div className="wizard-card space-y-6 animate-slide-up">
      {/* Header */}
      <div className="space-y-1">
        <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Step 8 of 9</p>
        <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">Download your visa package</h2>
        <p className="text-sm text-gray-500">
          Your complete {data.destinationFlag} {data.destinationCountry} application package is ready.
        </p>
      </div>

      {/* Package summary card */}
      <div className="bg-gradient-to-br from-blue-50 to-sky-50 border border-blue-100 rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm">
            <Package size={22} className="text-white" />
          </div>
          <div>
            <p className="font-extrabold text-gray-900">VisaPilot Application Package</p>
            <p className="text-xs text-gray-500">
              {data.destinationFlag} {data.destinationCountry} — {data.visaType} visa · {totalPages} pages · PDF
            </p>
          </div>
        </div>

        {/* Contents */}
        <div className="grid sm:grid-cols-2 gap-2">
          {packageContents.map((item) => (
            <div key={item.id} className="flex items-center gap-2.5 bg-white/80 rounded-xl p-2.5 border border-blue-100/50">
              <item.icon size={14} className={`text-${item.color}-500 shrink-0`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-gray-800 truncate">{item.label}</p>
                <p className="text-xs text-gray-400 truncate">{item.detail}</p>
              </div>
              <span className="text-xs text-gray-400 font-mono shrink-0">{item.pages}p</span>
            </div>
          ))}
        </div>
      </div>

      {/* Language export */}
      <div className="space-y-2">
        <label className="text-sm font-bold text-gray-700">Export language</label>
        <p className="text-xs text-gray-400">AI-generated documents will be translated to your selected language</p>
        <div className="grid grid-cols-4 gap-2">
          {languages.map((lang) => (
            <button
              key={`export-lang-${lang.code}`}
              onClick={() => setExportLang(lang.code)}
              className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border text-center transition-all duration-150 ${
                exportLang === lang.code
                  ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' :'border-gray-200 bg-white hover:border-blue-200'
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className={`text-xs font-bold ${exportLang === lang.code ? 'text-blue-700' : 'text-gray-600'}`}>
                {lang.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Download button */}
      <button
        onClick={handleDownload}
        disabled={downloading || downloaded}
        className={`w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-bold text-sm transition-all duration-150 active:scale-95 ${
          downloaded
            ? 'bg-green-500 text-white cursor-default' :'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
        }`}
      >
        {downloading ? (
          <><div className="w-4 h-4 rounded-full border-2 border-white/50 border-t-white animate-spin" /> Generating PDF...</>
        ) : downloaded ? (
          <><CheckCircle2 size={16} /> Package downloaded</>
        ) : (
          <><Download size={16} /> Download complete package (PDF)</>
        )}
      </button>

      {downloaded && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-100 rounded-xl animate-fade-in">
          <Sparkles size={13} className="text-green-500 shrink-0" />
          <p className="text-xs text-green-700 font-medium">
            VisaPilot_Germany_Tourist_Package.pdf downloaded. Submit to the German embassy or VFS Global.
          </p>
        </div>
      )}

      {/* Encryption notice */}
      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
        <Shield size={13} className="text-blue-400 shrink-0" />
        <p className="text-xs text-gray-500">
          Your package link expires in 72 hours. Re-download anytime from your dashboard.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={onBack} className="btn-secondary py-2.5 px-4 text-sm">
          <ChevronLeft size={15} />
          Back
        </button>
        <button onClick={onNext} className="btn-primary flex-1 justify-center py-2.5 text-sm">
          Complete payment
          <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}